import { useCallback } from 'react';

import type { Descendant } from 'platejs';
import type { Range, Editor as SlateEditor } from 'slate';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

import { buildSelectionMentionV3 } from '../chat-utils/buildSelectionMentionV3';
import type { AlineMarkdownEditorRef } from '../ChatPanel/AlineMarkdownEditor';
import { focusEditor } from '../ChatPanel/ChatEditor';

/**
 * Inserts a v3 selection mention into the chat input. The caller supplies an
 * explicit Slate range (e.g., the marker's range) instead of relying on
 * `editor.selection`, plus an optional `focusSuggestionId` to identify the
 * suggestion the user is asking to refine.
 */
export type InsertSelectionMentionV3Fn = (
  editor: SlateEditor,
  range: Range,
  options?: {
    focusSuggestionId?: string;
    replacePrompt?: boolean;
    textAfter?: string;
    textBefore?: string;
  }
) => void;

/**
 * Owns the side effects around inserting a v3 selection mention into the chat
 * input: validates + builds the mention via `buildSelectionMentionV3`, surfaces
 * size-overflow and unexpected-error toasts, mutates the Plate editor, and
 * refocuses it. Pure validation/build logic lives in the helper so it can be
 * unit-tested in isolation.
 */
export function useInsertSelectionMentionV3(
  markdownEditorRef: React.RefObject<AlineMarkdownEditorRef | null>
): InsertSelectionMentionV3Fn {
  const dispatch = useNotificationDispatch();

  return useCallback(
    (alineEditor, range, options = {}) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor) return;

      try {
        const result = buildSelectionMentionV3(alineEditor, range, {
          focusSuggestionId: options.focusSuggestionId,
        });
        if (result.status === 'invalid') return;
        if (result.status === 'too-large') {
          dispatch({
            payload: {
              key: uuid4(),
              message: 'Selection too large. Please select less text.',
              variant: 'warning',
            },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });
          return;
        }

        editor.tf.withoutNormalizing(() => {
          const nodesToInsert: Descendant[] = [result.mention];
          if (options.textBefore) nodesToInsert.unshift({ text: options.textBefore });
          if (options.textAfter) nodesToInsert.push({ text: options.textAfter });
          if (options.replacePrompt) editor.tf.reset();
          editor.tf.insertNodes(nodesToInsert);
        });

        focusEditor(editor);
      } catch (error) {
        console.error('Failed to insert selection mention v3:', error);
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Failed to insert selection mention.',
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      }
    },
    [dispatch, markdownEditorRef]
  );
}
